// Pure helpers for the sample timeline waterfall. Kept free of React so the
// layout/aggregation logic can be unit-tested directly.
import type { SpanCategory, TimelineSpan } from '../types/timeline';

// CSS variables work in SVG fills and keep category identities consistent
// while adapting their contrast to the current theme.
export const CATEGORY_COLORS: Record<SpanCategory, string> = {
  setup: 'var(--color-timeline-setup)',
  run: 'var(--color-timeline-run)',
  sample: 'var(--color-timeline-sample)',
  execution: 'var(--color-timeline-execution)',
  scoring: 'var(--color-timeline-scoring)',
  model: 'var(--color-timeline-model)',
  tool: 'var(--color-timeline-tool)',
  middleman: 'var(--color-timeline-middleman)',
  provider: 'var(--color-timeline-provider)',
  heartbeat: 'var(--color-timeline-heartbeat)',
  other: 'var(--color-timeline-other)',
};

export const CATEGORY_LABELS: Record<SpanCategory, string> = {
  setup: 'Setup',
  run: 'Run',
  sample: 'Sample',
  execution: 'Execution',
  scoring: 'Scoring',
  model: 'Model',
  tool: 'Tool',
  middleman: 'Middleman',
  provider: 'Provider',
  heartbeat: 'Heartbeat',
  other: 'Other',
};

// Tool calls at/above this wall-clock duration are flagged as "long" so the
// UI can surface them (a key HWK-27 goal).
export const LONG_TOOL_MS = 30_000;

export function colorForCategory(category: SpanCategory): string {
  return CATEGORY_COLORS[category] ?? CATEGORY_COLORS.other;
}

/**
 * Display name for a span: generic "tool" spans get the tool function
 * appended ("tool · python"). Arguments are deliberately never recorded on
 * spans (secrets/PII), so the tool name is the most specific label we have.
 */
export function spanDisplayName(span: TimelineSpan): string {
  if (span.name !== 'tool') return span.name;
  const tool = span.attributes['inspect.tool.name'];
  return typeof tool === 'string' && tool !== '' && tool !== span.name
    ? `${span.name} · ${tool}`
    : span.name;
}

/** Human-readable duration from milliseconds (e.g. 850ms, 4.2s, 3.5m, 1.2h). */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 2 : 1)}s`;
  if (s < 3600) return `${(s / 60).toFixed(1)}m`;
  if (s < 86400) return `${(s / 3600).toFixed(1)}h`;
  return `${(s / 86400).toFixed(1)}d`;
}

export interface TimelineRow {
  span: TimelineSpan;
  depth: number;
  /** Position within the timeline window, 0–100. */
  leftPct: number;
  widthPct: number;
  isLong: boolean;
}

export interface CategoryBreakdownEntry {
  category: SpanCategory | 'idle';
  ms: number;
  pct: number;
}

export interface TimelineModel {
  startMs: number;
  endMs: number;
  totalMs: number;
  rows: TimelineRow[];
  breakdown: CategoryBreakdownEntry[];
  modelMs: number;
  toolMs: number;
  scoringMs: number;
  setupMs: number;
  waitingMs: number;
  longToolCount: number;
  spanCount: number;
}

function waitingSeconds(span: TimelineSpan): number {
  const v = span.attributes['inspect.sample.waiting_time_s'];
  // typeof accepts NaN/Infinity; guard so they don't poison Math.max → NaN.
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : 0;
}

/** DFS span tree ordered by start time; depth = nesting level for indentation. */
function orderRows(
  spans: TimelineSpan[],
  startMs: number,
  totalMs: number
): TimelineRow[] {
  const byId = new Map(spans.map(s => [s.span_id, s]));
  const childrenOf = new Map<string | null, TimelineSpan[]>();
  for (const span of spans) {
    // A span whose parent isn't in this result set is treated as a root, so
    // the eval_set.run / runner.* context spans (whose parents live in other
    // services) still anchor the tree.
    const key =
      span.parent_id && byId.has(span.parent_id) ? span.parent_id : null;
    const list = childrenOf.get(key);
    if (list) list.push(span);
    else childrenOf.set(key, [span]);
  }
  for (const list of childrenOf.values()) {
    list.sort((a, b) => a.start_ms - b.start_ms || b.end_ms - a.end_ms);
  }

  const rows: TimelineRow[] = [];
  const seen = new Set<string>();
  const pushRow = (span: TimelineSpan, depth: number): void => {
    const widthMs = Math.max(span.duration_ms, 0);
    rows.push({
      span,
      depth,
      leftPct: totalMs > 0 ? ((span.start_ms - startMs) / totalMs) * 100 : 0,
      widthPct: totalMs > 0 ? (widthMs / totalMs) * 100 : 0,
      isLong: span.category === 'tool' && span.duration_ms >= LONG_TOOL_MS,
    });
  };
  const walk = (parentKey: string | null, depth: number): void => {
    for (const span of childrenOf.get(parentKey) ?? []) {
      if (seen.has(span.span_id)) continue; // parent_id cycle guard
      seen.add(span.span_id);
      pushRow(span, depth);
      walk(span.span_id, depth + 1);
    }
  };
  walk(null, 0);
  // A self-parent or parent_id cycle leaves spans unreachable from a root;
  // surface them at depth 0 so nothing is silently dropped from the waterfall.
  for (const span of spans) {
    if (!seen.has(span.span_id)) {
      seen.add(span.span_id);
      pushRow(span, 0);
    }
  }
  return rows;
}

function sumDuration(
  spans: TimelineSpan[],
  predicate: (s: TimelineSpan) => boolean
): number {
  return spans.reduce(
    (acc, s) => (predicate(s) ? acc + s.duration_ms : acc),
    0
  );
}

/**
 * Build the renderable model from raw spans, or null when there's nothing to
 * show. Category totals deliberately use specific span types to avoid
 * double-counting nested spans. Model time sums model.usage spans (backdated
 * over each successful call); model.generate is an instant marker emitted
 * only to inject trace headers, so it carries ~zero duration.
 */
export function buildTimelineModel(
  spans: TimelineSpan[]
): TimelineModel | null {
  if (spans.length === 0) return null;

  // Coerce numeric fields to finite values: a NaN/Infinity from malformed span
  // data would otherwise poison the window (Math.min/max → NaN) and cascade into
  // every derived percentage.
  spans = spans.map(s => ({
    ...s,
    start_ms: Number.isFinite(s.start_ms) ? s.start_ms : 0,
    end_ms: Number.isFinite(s.end_ms) ? s.end_ms : 0,
    duration_ms: Number.isFinite(s.duration_ms) ? s.duration_ms : 0,
  }));

  const startMs = Math.min(...spans.map(s => s.start_ms));
  const endMs = Math.max(...spans.map(s => s.end_ms));
  const totalMs = Math.max(endMs - startMs, 0);

  const modelMs = sumDuration(spans, s => s.name === 'model.usage');
  const toolMs = sumDuration(spans, s => s.category === 'tool');
  const scoringMs = sumDuration(spans, s => s.name === 'sample.scoring');
  // runner.setup is the umbrella; fall back to its children if it's absent so
  // setup time isn't double-counted with venv_create/pip_install.
  const setupUmbrella = sumDuration(spans, s => s.name === 'runner.setup');
  const setupMs =
    setupUmbrella > 0
      ? setupUmbrella
      : sumDuration(
          spans,
          s =>
            s.name === 'runner.venv_create' || s.name === 'runner.pip_install'
        );
  // waiting_time_s is cumulative per sample; take the largest reported value.
  const waitingMs = Math.max(0, ...spans.map(s => waitingSeconds(s) * 1000));

  const longToolCount = spans.filter(
    s => s.category === 'tool' && s.duration_ms >= LONG_TOOL_MS
  ).length;

  const segments: [SpanCategory, number][] = [
    ['setup', setupMs],
    ['model', modelMs],
    ['tool', toolMs],
    ['scoring', scoringMs],
  ];
  const accounted = segments.reduce((acc, [, ms]) => acc + ms, 0);
  // Category spans can overlap (e.g. scoring during a long tool call), so
  // `accounted` may exceed wall-clock. Normalise segment widths against the
  // larger of the two so the stacked bar never sums past 100% and clips.
  const denom = Math.max(totalMs, accounted);
  const breakdown: CategoryBreakdownEntry[] = segments
    .filter(([, ms]) => ms > 0)
    .map(([category, ms]) => ({
      category,
      ms,
      pct: denom > 0 ? (ms / denom) * 100 : 0,
    }));
  const idleMs = Math.max(0, totalMs - accounted);
  if (idleMs > 0 && denom > 0) {
    breakdown.push({
      category: 'idle',
      ms: idleMs,
      pct: (idleMs / denom) * 100,
    });
  }

  return {
    startMs,
    endMs,
    totalMs,
    rows: orderRows(spans, startMs, totalMs),
    breakdown,
    modelMs,
    toolMs,
    scoringMs,
    setupMs,
    waitingMs,
    longToolCount,
    spanCount: spans.length,
  };
}
